import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Bell, ChevronDown, ShieldCheck, ShieldAlert, ShieldX, Info
} from 'lucide-react';
import { useMQTT } from '../context/MQTTContext';
import { computeRisk } from '../lib/ai';

const SEV_META = {
  critical: { Icon: ShieldX,     fg: '#ef4444', bg: 'rgba(239,68,68,0.10)', label: 'Critical' },
  warning:  { Icon: ShieldAlert, fg: '#f59e0b', bg: 'rgba(245,158,11,0.10)', label: 'Warning'  },
  caution:  { Icon: Info,        fg: '#3b82f6', bg: 'rgba(59,130,246,0.10)', label: 'Caution'  },
  info:     { Icon: Info,        fg: '#3b82f6', bg: 'rgba(59,130,246,0.10)', label: 'Info'     },
  ok:       { Icon: ShieldCheck, fg: '#10b981', bg: 'rgba(16,185,129,0.10)', label: 'OK'       }
};

export default function AlertsPage() {
  const { sensorData } = useMQTT();
  const { findings, score, level } = computeRisk(sensorData);
  const [openIdx, setOpenIdx] = useState(null);

  return (
    <div className="grid gap-4 sm:gap-5">
      <motion.div layout className="card-surface p-5 flex items-center gap-3">
        <span className="w-11 h-11 rounded-2xl bg-gradient-to-br from-rose-500 to-fuchsia-500 grid place-items-center text-white shadow-glow">
          <Bell className="w-5 h-5" />
        </span>
        <div className="flex-1">
          <h2 className="text-lg font-extrabold tracking-tight">Alerts</h2>
          <p className="text-sm opacity-70">
            Risk score <span className="font-bold tabular-nums">{score}</span> ·
            level <span className="font-bold uppercase">{level}</span>
          </p>
        </div>
        <span className="chip bg-rose-500/15 text-rose-600 dark:text-rose-400">
          {findings.length} {findings.length === 1 ? 'event' : 'events'}
        </span>
      </motion.div>

      <div className="grid gap-3">
        {findings.map((f, i) => {
          const meta = SEV_META[f.severity] || SEV_META.info;
          const Icon = meta.Icon;
          const open = openIdx === i;
          return (
            <motion.button
              layout
              key={i}
              onClick={() => setOpenIdx(o => (o === i ? null : i))}
              whileTap={{ scale: 0.99 }}
              className="card-surface p-4 text-left ring-1 ring-transparent hover:ring-indigo-500/30 transition"
            >
              <div className="flex items-start gap-3">
                <span
                  className="w-10 h-10 shrink-0 rounded-2xl grid place-items-center"
                  style={{ background: meta.bg, color: meta.fg }}
                >
                  <Icon className="w-5 h-5" />
                </span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="chip" style={{ background: meta.bg, color: meta.fg }}>
                      {meta.label}
                    </span>
                    <p className="font-bold truncate">{f.title}</p>
                  </div>
                  <p className="text-sm opacity-70 mt-1">{f.detail}</p>
                  <AnimatePresence initial={false}>
                    {open && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        className="overflow-hidden"
                      >
                        <div className="mt-3 p-3 rounded-2xl bg-black/[0.04] dark:bg-white/[0.05]">
                          <p className="text-xs uppercase tracking-widest font-bold opacity-60">Recommended action</p>
                          <p className="text-sm font-semibold mt-1" style={{ color: meta.fg }}>
                            → {f.action}
                          </p>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
                <ChevronDown
                  className={`w-4 h-4 mt-2 opacity-60 transition-transform ${open ? 'rotate-180' : ''}`}
                />
              </div>
            </motion.button>
          );
        })}
      </div>
    </div>
  );
}
